import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  Users,
  UserPlus,
  Settings,
  LogOut,
  Sparkles,
  TrendingUp,
  DollarSign,
  Activity,
  Crown,
  Shield,
  Zap,
  Database,
  RefreshCw,
  Eye,
  Trash2,
  Edit,
  ChevronDown,
  Search,
  Filter,
  Download,
  Bell,
  MessageSquare,
  Calendar,
  Star,
  CheckCircle,
  AlertTriangle,
  Clock,
  Globe,
  Smartphone,
  Mail,
  Phone
} from "lucide-react";

// Simulated global stats
const globalStats = {
  totalNutritionists: 127,
  totalAthletes: 342,
  totalClients: 1580,
  activeSubscriptions: 98,
  monthlyRevenue: 8420,
  avgAdherence: 87,
  checkinsToday: 2341,
  newUsersThisWeek: 23
};

// Simulated users
const generateSimulatedUsers = () => {
  const nutritionists = [
    { id: "nutri_1", name: "Dra. María González", email: "maria@nutricoach.com", type: "nutritionist", plan: "Professional", clients: 28, status: "active", createdAt: "2025-01-15", revenue: 59 },
    { id: "nutri_2", name: "Dr. Carlos Mendez", email: "carlos@fitnutri.pe", type: "nutritionist", plan: "Scale", clients: 52, status: "active", createdAt: "2024-11-20", revenue: 99 },
    { id: "nutri_3", name: "Lic. Ana Torres", email: "ana.torres@gmail.com", type: "nutritionist", plan: "Starter", clients: 8, status: "active", createdAt: "2025-03-01", revenue: 29 },
    { id: "nutri_4", name: "FitInfluencer Pro", email: "influencer@fit.com", type: "influencer", plan: "Enterprise", clients: 156, status: "active", createdAt: "2024-09-10", revenue: 299 },
    { id: "nutri_5", name: "Dr. Roberto Silva", email: "roberto@clinic.com", type: "nutritionist", plan: "Professional", clients: 31, status: "suspended", createdAt: "2024-08-05", revenue: 0 },
  ];

  const athletes = [
    { id: "athlete_1", name: "Juan Pérez", email: "juan@athlete.com", type: "athlete", plan: "Athlete Pro", status: "active", streak: 45, createdAt: "2025-02-10" },
    { id: "athlete_2", name: "Laura García", email: "laura@runner.com", type: "athlete", plan: "Athlete Basic", status: "active", streak: 21, createdAt: "2025-03-15" },
    { id: "athlete_3", name: "Miguel Santos", email: "miguel@gym.com", type: "athlete", plan: "Athlete Pro", status: "active", streak: 67, createdAt: "2024-12-01" },
    { id: "athlete_4", name: "Sofia Reyes", email: "sofia@fitness.com", type: "athlete", plan: "Athlete Basic", status: "trial", streak: 7, createdAt: "2025-04-01" },
  ];

  return { nutritionists, athletes };
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [users, setUsers] = useState({ nutritionists: [], athletes: [] });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createType, setCreateType] = useState("nutritionist");
  const [isGeneratingData, setIsGeneratingData] = useState(false);
  
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    type: "nutritionist",
    plan: "Professional",
    simulatedClients: 10,
    isInfluencer: false
  });

  useEffect(() => {
    // Check admin auth
    const adminToken = localStorage.getItem("habitia_admin_token");
    if (!adminToken) {
      navigate("/admin/login");
      return;
    }
    
    // Load data
    setTimeout(() => {
      setUsers(generateSimulatedUsers());
      setLoading(false);
    }, 500);
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("habitia_admin_token");
    navigate("/admin/login");
  };

  const handleCreateUser = async () => {
    setIsGeneratingData(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    const newId = `${newUser.type}_${Date.now()}`;
    const newUserData = {
      id: newId,
      name: newUser.name,
      email: newUser.email,
      type: newUser.isInfluencer ? "influencer" : newUser.type,
      plan: newUser.plan,
      clients: newUser.type === "nutritionist" ? newUser.simulatedClients : undefined,
      streak: newUser.type === "athlete" ? 0 : undefined,
      status: "active",
      createdAt: new Date().toISOString().split('T')[0],
      revenue: newUser.type === "nutritionist" ? (newUser.plan === "Starter" ? 29 : newUser.plan === "Professional" ? 59 : 99) : 0
    };

    if (newUser.type === "nutritionist" || newUser.isInfluencer) {
      setUsers(prev => ({
        ...prev,
        nutritionists: [newUserData, ...prev.nutritionists]
      }));
    } else {
      setUsers(prev => ({
        ...prev,
        athletes: [newUserData, ...prev.athletes]
      }));
    }

    toast.success(`✅ ${newUser.isInfluencer ? 'Influencer' : newUser.type === 'nutritionist' ? 'Nutricionista' : 'Atleta'} creado con ${newUser.simulatedClients} clientes simulados`);
    setIsCreateModalOpen(false);
    setNewUser({
      name: "",
      email: "",
      type: "nutritionist",
      plan: "Professional",
      simulatedClients: 10,
      isInfluencer: false
    });
    setIsGeneratingData(false);
  };

  const handleGenerateSimulatedData = async (userId, count) => {
    setIsGeneratingData(true);
    
    // Simulate generating data
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setUsers(prev => ({
      ...prev,
      nutritionists: prev.nutritionists.map(n => 
        n.id === userId ? { ...n, clients: n.clients + count } : n
      )
    }));

    toast.success(`✅ ${count} clientes simulados generados exitosamente`);
    setIsGeneratingData(false);
  };

  const handleDeleteUser = (userId, type) => {
    if (!window.confirm("¿Estás seguro de eliminar este usuario?")) return;
    
    if (type === "nutritionist" || type === "influencer") {
      setUsers(prev => ({
        ...prev,
        nutritionists: prev.nutritionists.filter(n => n.id !== userId)
      }));
    } else {
      setUsers(prev => ({
        ...prev,
        athletes: prev.athletes.filter(a => a.id !== userId)
      }));
    }
    toast.success("Usuario eliminado");
  };

  const filteredNutritionists = users.nutritionists.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === "all" || user.type === filterType || user.status === filterType;
    return matchesSearch && matchesFilter;
  });

  const filteredAthletes = users.athletes.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 bg-purple-600 rounded-2xl flex items-center justify-center animate-pulse">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <p className="text-white/80">Cargando Admin Panel...</p>
        </div>
      </div>
    );
  }

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Nutricionistas", value: globalStats.totalNutritionists, icon: Users, color: "from-blue-500 to-cyan-500", change: "+12%" },
          { label: "Atletas", value: globalStats.totalAthletes, icon: Activity, color: "from-green-500 to-emerald-500", change: "+28%" },
          { label: "Clientes Totales", value: globalStats.totalClients.toLocaleString(), icon: Globe, color: "from-purple-500 to-pink-500", change: "+45%" },
          { label: "Revenue Mensual", value: `$${globalStats.monthlyRevenue.toLocaleString()}`, icon: DollarSign, color: "from-amber-500 to-orange-500", change: "+18%" },
        ].map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="bg-gray-800 border border-gray-700 rounded-2xl p-5 relative overflow-hidden"
          >
            <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${stat.color} opacity-10 rounded-full -translate-y-1/2 translate-x-1/2`}></div>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-gray-400 text-sm">{stat.label}</p>
                <p className="text-2xl font-bold text-white mt-1">{stat.value}</p>
                <p className="text-green-400 text-xs mt-2">{stat.change} este mes</p>
              </div>
              <div className={`w-10 h-10 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center`}>
                <stat.icon className="w-5 h-5 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Suscripciones Activas", value: globalStats.activeSubscriptions, icon: Crown },
          { label: "Check-ins Hoy", value: globalStats.checkinsToday.toLocaleString(), icon: CheckCircle },
          { label: "Adherencia Promedio", value: `${globalStats.avgAdherence}%`, icon: TrendingUp },
          { label: "Nuevos (7 días)", value: globalStats.newUsersThisWeek, icon: UserPlus },
        ].map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 + i * 0.1 }}
            className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-4"
          >
            <div className="flex items-center gap-3">
              <stat.icon className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-white font-semibold">{stat.value}</p>
                <p className="text-gray-500 text-xs">{stat.label}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-amber-400" />
          Acciones Rápidas
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Button
            onClick={() => { setCreateType("nutritionist"); setNewUser({...newUser, type: "nutritionist", isInfluencer: false}); setIsCreateModalOpen(true); }}
            className="bg-blue-600 hover:bg-blue-700 h-auto py-4 flex-col gap-2"
          >
            <Users className="w-5 h-5" />
            <span className="text-xs">Crear Nutricionista</span>
          </Button>
          <Button
            onClick={() => { setCreateType("influencer"); setNewUser({...newUser, type: "nutritionist", isInfluencer: true}); setIsCreateModalOpen(true); }}
            className="bg-purple-600 hover:bg-purple-700 h-auto py-4 flex-col gap-2"
          >
            <Star className="w-5 h-5" />
            <span className="text-xs">Crear Influencer</span>
          </Button>
          <Button
            onClick={() => { setCreateType("athlete"); setNewUser({...newUser, type: "athlete", isInfluencer: false}); setIsCreateModalOpen(true); }}
            className="bg-green-600 hover:bg-green-700 h-auto py-4 flex-col gap-2"
          >
            <Activity className="w-5 h-5" />
            <span className="text-xs">Crear Atleta</span>
          </Button>
          <Button
            variant="outline"
            className="border-gray-600 text-gray-300 hover:bg-gray-700 h-auto py-4 flex-col gap-2"
          >
            <Download className="w-5 h-5" />
            <span className="text-xs">Exportar Datos</span>
          </Button>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <Activity className="w-5 h-5 text-green-400" />
          Actividad Reciente
        </h3>
        <div className="space-y-3">
          {[
            { action: "Nuevo nutricionista registrado", user: "Dr. Pedro García", time: "Hace 5 min", type: "success" },
            { action: "Upgrade a Plan Scale", user: "Lic. María López", time: "Hace 15 min", type: "info" },
            { action: "Nuevo atleta independiente", user: "Carlos Ruiz", time: "Hace 1 hora", type: "success" },
            { action: "Suscripción cancelada", user: "Ana Torres", time: "Hace 2 horas", type: "warning" },
            { action: "100 clientes simulados generados", user: "FitInfluencer Pro", time: "Hace 3 horas", type: "info" },
          ].map((activity, i) => (
            <div key={i} className="flex items-center justify-between p-3 bg-gray-700/30 rounded-xl">
              <div className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${
                  activity.type === 'success' ? 'bg-green-400' :
                  activity.type === 'warning' ? 'bg-amber-400' : 'bg-blue-400'
                }`}></div>
                <div>
                  <p className="text-white text-sm">{activity.action}</p>
                  <p className="text-gray-500 text-xs">{activity.user}</p>
                </div>
              </div>
              <span className="text-gray-500 text-xs">{activity.time}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderUsers = () => (
    <div className="space-y-6">
      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar usuarios..."
            className="pl-10 bg-gray-800 border-gray-700 text-white"
          />
        </div>
        <div className="flex gap-2">
          {["all", "nutritionist", "influencer", "active", "suspended"].map(filter => (
            <Button
              key={filter}
              variant={filterType === filter ? "default" : "outline"}
              size="sm"
              onClick={() => setFilterType(filter)}
              className={filterType === filter ? "bg-purple-600" : "border-gray-600 text-gray-300"}
            >
              {filter === "all" ? "Todos" : 
               filter === "nutritionist" ? "Nutris" :
               filter === "influencer" ? "Influencers" :
               filter === "active" ? "Activos" : "Suspendidos"}
            </Button>
          ))}
        </div>
      </div>

      {/* Nutritionists Table */}
      <div className="bg-gray-800 border border-gray-700 rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-gray-700">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-400" />
            Nutricionistas & Influencers ({filteredNutritionists.length})
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700 text-gray-400 text-xs uppercase">
                <th className="p-4 text-left">Usuario</th>
                <th className="p-4 text-left">Tipo</th>
                <th className="p-4 text-left">Plan</th>
                <th className="p-4 text-left">Clientes</th>
                <th className="p-4 text-left">Estado</th>
                <th className="p-4 text-left">Revenue</th>
                <th className="p-4 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredNutritionists.map((user) => (
                <tr key={user.id} className="border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        user.type === 'influencer' ? 'bg-purple-600' : 'bg-blue-600'
                      }`}>
                        <span className="text-white font-bold text-sm">{user.name.charAt(0)}</span>
                      </div>
                      <div>
                        <p className="text-white font-medium">{user.name}</p>
                        <p className="text-gray-500 text-xs">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      user.type === 'influencer' 
                        ? 'bg-purple-500/20 text-purple-400' 
                        : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {user.type === 'influencer' ? '⭐ Influencer' : 'Nutricionista'}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className="text-gray-300">{user.plan}</span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium">{user.clients}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-xs text-green-400 hover:text-green-300 hover:bg-green-500/10"
                        onClick={() => handleGenerateSimulatedData(user.id, 10)}
                        disabled={isGeneratingData}
                      >
                        +10
                      </Button>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      user.status === 'active' 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                      {user.status === 'active' ? 'Activo' : 'Suspendido'}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className="text-green-400 font-medium">${user.revenue}/mes</span>
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-gray-400 hover:text-white">
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-gray-400 hover:text-white">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-8 w-8 p-0 text-gray-400 hover:text-red-400"
                        onClick={() => handleDeleteUser(user.id, user.type)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Athletes Table */}
      <div className="bg-gray-800 border border-gray-700 rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-gray-700">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Activity className="w-5 h-5 text-green-400" />
            Atletas Independientes ({filteredAthletes.length})
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700 text-gray-400 text-xs uppercase">
                <th className="p-4 text-left">Usuario</th>
                <th className="p-4 text-left">Plan</th>
                <th className="p-4 text-left">Racha</th>
                <th className="p-4 text-left">Estado</th>
                <th className="p-4 text-left">Registro</th>
                <th className="p-4 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredAthletes.map((user) => (
                <tr key={user.id} className="border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-sm">{user.name.charAt(0)}</span>
                      </div>
                      <div>
                        <p className="text-white font-medium">{user.name}</p>
                        <p className="text-gray-500 text-xs">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-gray-300">{user.plan}</span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-1.5">
                      <span className="text-orange-400">🔥</span>
                      <span className="text-white font-medium">{user.streak} días</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      user.status === 'active' 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-amber-500/20 text-amber-400'
                    }`}>
                      {user.status === 'active' ? 'Activo' : 'Trial'}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className="text-gray-400 text-sm">{user.createdAt}</span>
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-gray-400 hover:text-white">
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-8 w-8 p-0 text-gray-400 hover:text-red-400"
                        onClick={() => handleDeleteUser(user.id, "athlete")}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-6">
      <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6">
        <h3 className="text-white font-semibold mb-4">Configuración del Sistema</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-700/30 rounded-xl">
            <div>
              <p className="text-white font-medium">Modo de Mantenimiento</p>
              <p className="text-gray-500 text-sm">Desactiva el acceso a usuarios temporalmente</p>
            </div>
            <Switch />
          </div>
          
          <div className="flex items-center justify-between p-4 bg-gray-700/30 rounded-xl">
            <div>
              <p className="text-white font-medium">Registros Nuevos</p>
              <p className="text-gray-500 text-sm">Permite nuevos registros de usuarios</p>
            </div>
            <Switch defaultChecked />
          </div>
          
          <div className="flex items-center justify-between p-4 bg-gray-700/30 rounded-xl">
            <div>
              <p className="text-white font-medium">Trial de 7 días</p>
              <p className="text-gray-500 text-sm">Activa prueba gratuita para nuevos usuarios</p>
            </div>
            <Switch defaultChecked />
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-700/30 rounded-xl">
            <div>
              <p className="text-white font-medium">Notificaciones WhatsApp</p>
              <p className="text-gray-500 text-sm">Envía notificaciones automáticas</p>
            </div>
            <Switch defaultChecked />
          </div>
        </div>
      </div>

      <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6">
        <h3 className="text-white font-semibold mb-4">Planes de Precio</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { name: "Starter", price: 29, users: 23, features: "10 clientes" },
            { name: "Professional", price: 59, users: 52, features: "30 clientes" },
            { name: "Scale", price: 99, users: 18, features: "60 clientes" },
            { name: "Enterprise", price: 299, users: 5, features: "Ilimitado" },
          ].map(plan => (
            <div key={plan.name} className="p-4 bg-gray-700/30 rounded-xl">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <p className="text-white font-medium">{plan.name}</p>
                  <p className="text-gray-500 text-sm">{plan.features}</p>
                </div>
                <span className="text-green-400 font-bold">${plan.price}</span>
              </div>
              <p className="text-gray-400 text-xs">{plan.users} usuarios activos</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6">
        <h3 className="text-white font-semibold mb-4">Planes Atletas</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { name: "Athlete Basic", price: 9, users: 156, features: "Tracking básico" },
            { name: "Athlete Pro", price: 19, users: 186, features: "IA + Análisis" },
          ].map(plan => (
            <div key={plan.name} className="p-4 bg-gray-700/30 rounded-xl">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <p className="text-white font-medium">{plan.name}</p>
                  <p className="text-gray-500 text-sm">{plan.features}</p>
                </div>
                <span className="text-green-400 font-bold">${plan.price}</span>
              </div>
              <p className="text-gray-400 text-xs">{plan.users} usuarios activos</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Sidebar */}
      <aside className="fixed top-0 left-0 bottom-0 w-64 bg-gray-800 border-r border-gray-700 p-4 hidden lg:flex flex-col">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-white font-bold">HabitIA</p>
            <p className="text-gray-500 text-xs">Admin Panel</p>
          </div>
        </div>

        <nav className="flex-1 space-y-2">
          {[
            { id: "overview", label: "Dashboard", icon: LayoutDashboard },
            { id: "users", label: "Usuarios", icon: Users },
            { id: "settings", label: "Configuración", icon: Settings },
          ].map(item => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                activeTab === item.id
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-400 hover:bg-gray-700 hover:text-white'
              }`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </button>
          ))}
        </nav>

        <Button
          variant="ghost"
          className="mt-auto text-gray-400 hover:text-white justify-start"
          onClick={handleLogout}
        >
          <LogOut className="w-4 h-4 mr-2" />
          Cerrar sesión
        </Button>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">
              {activeTab === "overview" ? "Dashboard" : 
               activeTab === "users" ? "Gestión de Usuarios" : "Configuración"}
            </h1>
            <p className="text-gray-500">Panel de administración de HabitIA</p>
          </div>

          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" className="border-gray-600 text-gray-300">
              <Bell className="w-4 h-4 mr-2" />
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
              </span>
            </Button>
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-800 rounded-xl border border-gray-700">
              <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">A</span>
              </div>
              <span className="text-white text-sm">Admin</span>
            </div>
          </div>
        </div>

        {/* Content */}
        {activeTab === "overview" && renderOverview()}
        {activeTab === "users" && renderUsers()}
        {activeTab === "settings" && renderSettings()}
      </main>

      {/* Create User Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">
              {newUser.isInfluencer ? "Crear Influencer" : 
               newUser.type === "nutritionist" ? "Crear Nutricionista" : "Crear Atleta"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-gray-300">Nombre completo</Label>
              <Input
                value={newUser.name}
                onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                placeholder="Dr. Juan Pérez"
                className="mt-1 bg-gray-700 border-gray-600 text-white"
              />
            </div>
            
            <div>
              <Label className="text-gray-300">Email</Label>
              <Input
                value={newUser.email}
                onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                placeholder="juan@example.com"
                className="mt-1 bg-gray-700 border-gray-600 text-white"
              />
            </div>

            {(newUser.type === "nutritionist" || newUser.isInfluencer) && (
              <>
                <div>
                  <Label className="text-gray-300">Plan</Label>
                  <select
                    value={newUser.plan}
                    onChange={(e) => setNewUser({...newUser, plan: e.target.value})}
                    className="w-full mt-1 bg-gray-700 border border-gray-600 text-white rounded-lg p-2"
                  >
                    <option value="Starter">Starter ($29/mes)</option>
                    <option value="Professional">Professional ($59/mes)</option>
                    <option value="Scale">Scale ($99/mes)</option>
                    {newUser.isInfluencer && <option value="Enterprise">Enterprise ($299/mes)</option>}
                  </select>
                </div>

                <div>
                  <Label className="text-gray-300">Clientes simulados a generar</Label>
                  <Input
                    type="number"
                    value={newUser.simulatedClients}
                    onChange={(e) => setNewUser({...newUser, simulatedClients: parseInt(e.target.value) || 0})}
                    min={0}
                    max={500}
                    className="mt-1 bg-gray-700 border-gray-600 text-white"
                  />
                  <p className="text-gray-500 text-xs mt-1">
                    Se crearán {newUser.simulatedClients} clientes con datos realistas
                  </p>
                </div>
              </>
            )}

            {newUser.type === "athlete" && (
              <div>
                <Label className="text-gray-300">Plan</Label>
                <select
                  value={newUser.plan}
                  onChange={(e) => setNewUser({...newUser, plan: e.target.value})}
                  className="w-full mt-1 bg-gray-700 border border-gray-600 text-white rounded-lg p-2"
                >
                  <option value="Athlete Basic">Athlete Basic ($9/mes)</option>
                  <option value="Athlete Pro">Athlete Pro ($19/mes)</option>
                </select>
              </div>
            )}

            <Button
              onClick={handleCreateUser}
              disabled={!newUser.name || !newUser.email || isGeneratingData}
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              {isGeneratingData ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Generando datos...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Crear {newUser.isInfluencer ? 'Influencer' : newUser.type === 'nutritionist' ? 'Nutricionista' : 'Atleta'}
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
